import React, { useMemo, useState } from 'react';
import { Container, Card, Table, Badge, Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { InfoCircle, CheckCircle, Plus } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { useAccount } from '../contexts/AccountContext';
import { useCreateAWSAccountMutation, useGetDomainsQuery } from '../services/api';
import { countDomainsPerHostingAccount } from '../utils/hostingAccountDomainMapping';
import EMMSectionHeader from './EMMSectionHeader';

/**
 * Hosting Management (AWS-only)
 *
 * This screen intentionally shows only AWS account connections.
 */
const AWSAccountCreation = () => {
  const {
    accounts: allAccountsFromCtx,
    workspaceAwsAccounts,
    selectedAccount,
    isAllAccountsSelected,
  } = useAccount();
  const accounts =
    workspaceAwsAccounts && workspaceAwsAccounts.length > 0 ? workspaceAwsAccounts : allAccountsFromCtx;
  const accountsForGrouping =
    allAccountsFromCtx && allAccountsFromCtx.length > 0 ? allAccountsFromCtx : accounts;

  const { data: domainsData, isLoading: domainsLoading, isError: hasDomainsQueryError } =
    useGetDomainsQuery();

  const resolvedDomainCounts = useMemo(() => {
    const rows = domainsData?.data?.domains || [];
    return countDomainsPerHostingAccount(rows, accountsForGrouping);
  }, [domainsData, accountsForGrouping]);

  const [createAWSAccount, { isLoading: isCreating }] = useCreateAWSAccountMutation();

  const [showAddModal, setShowAddModal] = useState(false);
  const [draft, setDraft] = useState({
    id: '',
    alias: '',
    name: '',
    region: 'us-east-1',
    roleArn: '',
    externalId: '',
  });

  const accountsToDisplay = useMemo(() => {
    if (isAllAccountsSelected) return accounts;
    return accounts.filter((a) => a.id === selectedAccount?.id);
  }, [accounts, isAllAccountsSelected, selectedAccount?.id]);

  const domainCountForAccount = (accountId) => {
    if (hasDomainsQueryError) {
      const a = accounts.find((x) => String(x.id) === String(accountId));
      return Array.isArray(a?.domains) ? a.domains.length : 0;
    }
    if (domainsLoading) return null;
    return resolvedDomainCounts[String(accountId)] ?? 0;
  };

  const canSubmit = (() => {
    const id = String(draft.id || '').trim();
    if (!/^\d{12}$/.test(id)) return false;
    if (!String(draft.name || '').trim()) return false;
    return true;
  })();

  const handleSubmit = async () => {
    try {
      const payload = {
        id: String(draft.id || '').trim(),
        alias: String(draft.alias || '').trim() || undefined,
        name: String(draft.name || '').trim(),
        region: String(draft.region || '').trim() || 'us-east-1',
        roleArn: String(draft.roleArn || '').trim() || undefined,
        externalId: String(draft.externalId || '').trim() || undefined,
      };
      await createAWSAccount(payload).unwrap();
      toast.success('AWS account connected');
      setShowAddModal(false);
      setDraft({
        id: '',
        alias: '',
        name: '',
        region: 'us-east-1',
        roleArn: '',
        externalId: '',
      });
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Failed to add AWS account');
    }
  };

  return (
    <>
      <EMMSectionHeader title="Hosting Management" showHelp />
      <Container className="py-3">
      {/* Action bar - same style as other pages */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={() => setShowAddModal(true)}
          title="Add AWS Hosting Account"
        >
          <Plus className="me-1" size={14} />
          Hosting Account
        </Button>
        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
          {accountsToDisplay.length} account{accountsToDisplay.length !== 1 ? 's' : ''}
        </span>
      </div>

      <p className="text-muted mb-3">
        Connect an AWS account (optional) so KG can manage Route53 DNS automatically. If you do not connect AWS, you can still configure DNS manually using the copy/paste records on the Domains page.
      </p>

      <Alert variant="info" className="mb-4">
        <InfoCircle className="me-2" />
        Inbound is Postmark-only; outbound is SMTP relay-based (send pools). AWS connections here are for DNS automation and infrastructure visibility.
      </Alert>

      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <CheckCircle className="me-2" />
            <strong>AWS Accounts</strong>
            <Badge bg="secondary">{accountsToDisplay.length}</Badge>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {accountsToDisplay.length === 0 ? (
            <div className="p-3">
              <Alert variant="secondary" className="mb-0">
                No AWS accounts connected.
              </Alert>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Alias</th>
                  <th>Status</th>
                  <th>Region</th>
                  <th>Domains</th>
                </tr>
              </thead>
              <tbody>
                {accountsToDisplay.map((a) => {
                  const n = domainCountForAccount(a.id);
                  return (
                  <tr key={a.id}>
                    <td className="fw-medium">{a.id}</td>
                    <td>{a.alias || a.name || '—'}</td>
                    <td>
                      <Badge bg={String(a.status || '').toLowerCase() === 'connected' ? 'success' : 'secondary'}>
                        {a.status || 'connected'}
                      </Badge>
                    </td>
                    <td>{a.region || 'us-east-1'}</td>
                    <td title="Count from domain records (same mapping as Domains page)">
                      {n === null ? '…' : n}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add AWS Hosting Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="secondary" className="mb-3">
            Connecting AWS is optional. If you skip it, you can still set up SPF/DKIM/DMARC by adding TXT records manually at your DNS host.
          </Alert>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>AWS Account ID</Form.Label>
              <Form.Control
                value={draft.id}
                onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))}
                placeholder="12-digit AWS Account ID"
                inputMode="numeric"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Account Name</Form.Label>
              <Form.Control
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Kross Global Marketing"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Alias (optional)</Form.Label>
              <Form.Control
                value={draft.alias}
                onChange={(e) => setDraft((d) => ({ ...d, alias: e.target.value }))}
                placeholder="Short label shown in UI"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Region</Form.Label>
              <Form.Control
                value={draft.region}
                onChange={(e) => setDraft((d) => ({ ...d, region: e.target.value }))}
                placeholder="us-east-1"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role ARN (optional)</Form.Label>
              <Form.Control
                value={draft.roleArn}
                onChange={(e) => setDraft((d) => ({ ...d, roleArn: e.target.value }))}
                placeholder="arn:aws:iam::123456789012:role/..."
              />
              <Form.Text className="text-muted">
                If you want KG to manage Route53 in a different AWS account, provide an assumable role.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>External ID (optional)</Form.Label>
              <Form.Control
                value={draft.externalId}
                onChange={(e) => setDraft((d) => ({ ...d, externalId: e.target.value }))}
                placeholder="External ID for role assumption"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit || isCreating}>
            {isCreating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default AWSAccountCreation;


